/**
 * Core state management module.
 *
 * Purpose: Single source of truth for annotation state per editor.
 * This module manages annotation data and selected annotations for each editor instance.
 *
 * Key responsibilities:
 * - Create and manage per-editor annotation state
 * - CRUD operations for annotations
 * - Track selected annotations
 * - Ensure state isolation between editor instances
 *
 * Dependencies: types/state.ts, types/common.ts
 */
import { Console } from '@/utils/console';
/**
 * Per-editor state storage
 * Maps editorId -> AnnotationState
 */
const stateMap = new Map();
/**
 * Creates a new annotation state for an editor instance.
 *
 * @param editorId Unique identifier for the editor
 * @returns New AnnotationState instance
 */
export const createAnnotationState = (editorId) => {
    const state = {
        annotations: new Map(),
        commentAnnotations: [], // Full CommentAnnotation[] with status for rendering
        selectedAnnotations: new Set(),
        editorId,
    };
    stateMap.set(editorId, state);
    return state;
};
/**
 * Gets the annotation state for an editor, creating it if it doesn't exist.
 *
 * @param editorId Unique identifier for the editor
 * @returns AnnotationState instance
 */
const getOrCreateState = (editorId) => {
    let state = stateMap.get(editorId);
    if (!state) {
        state = createAnnotationState(editorId);
    }
    return state;
};
/**
 * Updates annotations in state from Velt SDK data.
 * Converts CommentAnnotation[] to AnnotationData[] and stores them.
 *
 * @param editorId Unique identifier for the editor
 * @param annotations Array of CommentAnnotation from Velt SDK
 */
export const updateAnnotations = (editorId, annotations) => {
    try {
        const state = getOrCreateState(editorId);
        // Store full CommentAnnotation[] with status for rendering
        state.commentAnnotations = annotations;
        // Convert CommentAnnotation[] to AnnotationData[] for core logic
        const annotationDataMap = new Map();
        for (const annotation of annotations) {
            if (!(annotation === null || annotation === void 0 ? void 0 : annotation.annotationId)) {
                continue;
            }
            const annotationData = {
                annotationId: annotation.annotationId,
                multiThreadAnnotationId: annotation.multiThreadAnnotationId,
                context: annotation.context,
                // Position will be set when marks are applied
            };
            annotationDataMap.set(annotation.annotationId, annotationData);
        }
        // Update state
        state.annotations = annotationDataMap;
    }
    catch (error) {
        Console.catch('Error updating annotations:', error);
    }
};
/**
 * Retrieves a single annotation by ID.
 *
 * @param editorId Unique identifier for the editor
 * @param annotationId The annotation ID to retrieve
 * @returns AnnotationData or null if not found
 */
export const getAnnotation = (editorId, annotationId) => {
    try {
        const state = stateMap.get(editorId);
        if (!state) {
            return null;
        }
        return state.annotations.get(annotationId) || null;
    }
    catch (error) {
        Console.catch('Error getting annotation:', error);
        return null;
    }
};
/**
 * Removes an annotation from state.
 *
 * @param editorId Unique identifier for the editor
 * @param annotationId The annotation ID to remove
 */
export const removeAnnotation = (editorId, annotationId) => {
    try {
        const state = stateMap.get(editorId);
        if (!state) {
            return;
        }
        state.annotations.delete(annotationId);
        state.selectedAnnotations.delete(annotationId);
    }
    catch (error) {
        Console.catch('Error removing annotation:', error);
    }
};
/**
 * Gets the set of selected annotation IDs for an editor.
 *
 * @param editorId Unique identifier for the editor
 * @returns Set of selected annotation IDs
 */
export const getSelectedAnnotations = (editorId) => {
    try {
        const state = stateMap.get(editorId);
        if (!state) {
            return new Set();
        }
        return new Set(state.selectedAnnotations);
    }
    catch (error) {
        Console.catch('Error getting selected annotations:', error);
        return new Set();
    }
};
/**
 * Updates the set of selected annotation IDs for an editor.
 *
 * @param editorId Unique identifier for the editor
 * @param selectedIds Set of selected annotation IDs
 */
export const setSelectedAnnotations = (editorId, selectedIds) => {
    try {
        const state = getOrCreateState(editorId);
        state.selectedAnnotations = new Set(selectedIds);
    }
    catch (error) {
        Console.catch('Error setting selected annotations:', error);
    }
};
/**
 * Gets all annotations for an editor.
 *
 * @param editorId Unique identifier for the editor
 * @returns Map of annotation ID to AnnotationData
 */
export const getAllAnnotations = (editorId) => {
    try {
        const state = stateMap.get(editorId);
        if (!state) {
            return new Map();
        }
        return new Map(state.annotations);
    }
    catch (error) {
        Console.catch('Error getting all annotations:', error);
        return new Map();
    }
};
/**
 * Gets all comment annotations for an editor (with status field).
 *
 * @param editorId Unique identifier for the editor
 * @returns Array of CommentAnnotation with status field
 */
export const getCommentAnnotations = (editorId) => {
    try {
        const state = stateMap.get(editorId);
        if (!state) {
            return [];
        }
        return [...state.commentAnnotations];
    }
    catch (error) {
        Console.catch('Error getting comment annotations:', error);
        return [];
    }
};
/**
 * Clears all state for an editor (used during cleanup).
 *
 * @param editorId Unique identifier for the editor
 */
export const clearState = (editorId) => {
    stateMap.delete(editorId);
};
